import { useEffect, useState } from 'react';
import { Modal, Button, Textarea, Input } from '../../../shared/components';
import { SearchSelect } from '../../../shared/components/SearchSelect';
import { OperationTimeRecord, UpdateActivityInput } from '../../../shared/services/operationTimes/types';
import { ParamActivity } from '../../../shared/services/settings/types';

interface EditRecordModalProps {
  record: OperationTimeRecord | null;
  activities: ParamActivity[];
  onClose: () => void;
  onSubmit: (id: string, patch: UpdateActivityInput) => Promise<void>;
}

/**
 * Edicion de registros - "solo usuarios autorizados" segun reglas de negocio.
 * El ajuste manual de Inicio/Fin esta disponible para CUALQUIER rol con
 * permiso para editar el registro (Operador, Supervisor, Administrador) - no
 * esta restringido por rol (ver tambien service.ts en la API, que ya no
 * filtra estos campos segun rol).
 * La justificacion es OBLIGATORIA (alimenta la columna "Modificado / Borrado"
 * del historial): si se intenta guardar sin diligenciarla, el campo se resalta
 * en amarillo y se muestra una alerta, sin permitir continuar.
 */
export function EditRecordModal({ record, activities, onClose, onSubmit }: EditRecordModalProps) {
  const [activityId, setActivityId] = useState('');
  const [observations, setObservations] = useState('');
  const [reason, setReason] = useState('');
  const [reasonMissing, setReasonMissing] = useState(false);
  const [startLocal, setStartLocal] = useState('');
  const [endLocal, setEndLocal] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!record) return;
    setActivityId(record.activityId);
    setObservations(record.observations ?? '');
    setReason('');
    setReasonMissing(false);
    setStartLocal(toLocalInput(record.startDateTime));
    setEndLocal(toLocalInput(record.endDateTime));
  }, [record]);

  if (!record) return null;

  const handleSubmit = async () => {
    if (!reason.trim()) {
      setReasonMissing(true);
      return;
    }
    setIsSaving(true);
    try {
      const activity = activities.find((a) => a.id === activityId);
      const patch: UpdateActivityInput = {
        activityId,
        activityName: activity?.name,
        observations,
        reason: reason.trim(),
        startDateTime: new Date(startLocal).toISOString(),
        endDateTime: new Date(endLocal).toISOString(),
      };
      await onSubmit(record.id, patch);
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal open={!!record} onClose={onClose} title={`Editar registro ${record.code}`}>
      <div className="space-y-4">
        <SearchSelect label="Actividad" options={activities.map((a) => ({ value: a.id, label: a.name }))} value={activityId} onChange={setActivityId} />
        <Textarea label="Observaciones" value={observations} onChange={(e) => setObservations(e.target.value)} />
        <div className="grid grid-cols-2 gap-3">
          <Input label="Inicio" type="datetime-local" value={startLocal} onChange={(e) => setStartLocal(e.target.value)} />
          <Input label="Fin" type="datetime-local" value={endLocal} onChange={(e) => setEndLocal(e.target.value)} />
        </div>

        <div>
          <Textarea
            label="Motivo de la modificación *"
            placeholder="Ej: Se me olvidó cerrar la actividad y ya llevaba más de 3 horas, realmente fue una hora."
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              if (e.target.value.trim()) setReasonMissing(false);
            }}
            className={reasonMissing ? 'border-amber-400 bg-amber-50 ring-2 ring-amber-300' : ''}
          />
          {reasonMissing && <p className="mt-1 text-xs font-medium text-amber-700">⚠️ Debes justificar por qué se modifica este registro para poder guardar.</p>}
        </div>

        <div className="flex gap-3 pt-2">
          <Button variant="outline" fullWidth onClick={onClose}>Cancelar</Button>
          <Button fullWidth onClick={handleSubmit} isLoading={isSaving}>Guardar cambios</Button>
        </div>
      </div>
    </Modal>
  );
}

function toLocalInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
